import axios from "axios";
import { FHIR_METHODS } from "config/constants/adapter_constants";
import { getTenantFromToken } from "config/helpers/common_helper";
import { handleLogout, TokenValidate } from "config/helpers/logout_helper";
import { auth } from "firebase-setup";
import jwtDecode from "jwt-decode";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  change_app_language,
  change_login,
  reset_state,
} from "redux/actions/AppActions";
import { set_is_logging_out } from "redux/actions/AuthActions";
import { reset_subscription } from "redux/actions/SubscriptionAction";
import { constants } from "redux/constants/redux-constants";
import { store } from "redux/store";
import { getFirebaseIdTokenFromRefreshToken } from "../firebase-setup/firebaseHelpers";
import { setTokenInvalidGlobal } from "utils/session";

// Refresh queue logic to prevent concurrent refreshes and ensure all requests use latest token
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

const generateInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem("jwtToken");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  AxiosInstance.interceptors.response.use(
    (response) => {
      TokenValidate();
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        if (isRefreshing) {
          // Wait for refresh to finish and then retry
          return new Promise((resolve) => {
            addRefreshSubscriber((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(AxiosInstance(originalRequest));
            });
          });
        }

        isRefreshing = true;
        let newToken = null;

        try {
          const user = auth?.currentUser;
          if (user) {
            try {
              newToken = await user.getIdToken(true);
            } catch (err) {
              if (err?.code === "auth/user-token-expired") {
                console.error("User's Firebase token has expired. Logging out.");
                handleLogout();
                isRefreshing = false;
                onRefreshed(null);
                return Promise.resolve();
              }
              console.error("Error getting ID token:", err.message || err);
              handleLogout();
              isRefreshing = false;
              onRefreshed(null);
              return Promise.resolve();
            }
          } else {
            const refreshToken = localStorage.getItem("firebaseRefreshToken");
            const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
            if (!refreshToken || !apiKey) {
              console.error("Missing refreshToken or Firebase API key.");
              setTokenInvalidGlobal(true);
              isRefreshing = false;
              onRefreshed(null);
              return Promise.resolve();
            }
            try {
              const tokenResponse = await getFirebaseIdTokenFromRefreshToken(
                refreshToken,
                apiKey
              );
              newToken = tokenResponse.id_token;
              if (tokenResponse.refresh_token) {
                localStorage.setItem(
                  "firebaseRefreshToken",
                  tokenResponse.refresh_token
                );
              }
            } catch (refreshErr) {
              console.error(
                "Failed to refresh Firebase token:",
                refreshErr.message || refreshErr
              );
              setTokenInvalidGlobal(true);
              isRefreshing = false;
              onRefreshed(null);
              return Promise.resolve();
            }
          }

          if (newToken) {
            localStorage.setItem("jwtToken", newToken);
            // Notify all waiting requests with new token
            onRefreshed(newToken);
            isRefreshing = false;
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return AxiosInstance(originalRequest);
          } else {
            handleLogout();
            isRefreshing = false;
            onRefreshed(null);
            return Promise.resolve();
          }
        } catch (err) {
          console.error("Error during token refresh:", err.message || err);
          handleLogout();
          isRefreshing = false;
          onRefreshed(null);
          return Promise.resolve();
        }
      }
      return Promise.reject(error);
    }
  );
};

const generateMetaInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem(`jwtToken`);
      if (token) {
        const jwtPayload = jwtDecode(token);
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["x-hasura-role"] = `admin`;
        config.headers[
          "x-hasura-admin-secret"
        ] = `c042293c-7ae1-43c5-bda1-fc3519f5fac2`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

const generateBridgeRequestInterceptors = (AxiosInstance) => {
  AxiosInstance.interceptors.request.use(async (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });
};

export const HTTPRequest = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

generateInterceptors(HTTPRequest);

export const HTTPAidaBaseService = axios.create({
  baseURL: process.env.REACT_APP_AIDA_SERVICE_URL,
});

generateInterceptors(HTTPAidaBaseService);

let HTTPRequestUnauthenticated = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const HTTPBridgeService = axios.create({
  baseURL: process.env.REACT_APP_EHR_PROXY_URL,
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

export const HTTPLocalService = axios.create({
  baseURL: "http://localhost:8000",
  headers: {
    "x-api-key": process.env.REACT_APP_EHR_PROXY_API_KEY,
    "Content-Type": "application/json",
  },
});

// generateBridgeRequestInterceptors(HTTPLocalService);
// generateBridgeRequestInterceptors(HTTPBridgeService);

generateInterceptors(HTTPBridgeService);
generateInterceptors(HTTPLocalService);

export const HTTPVimService = axios.create({
  baseURL: "https://vim-dev-service-152128589088.us-east4.run.app",
  headers: {
    "Content-Type": "application/json",
  },
});

export const HTTPVimLocalService = axios.create({
  baseURL: "http://localhost:12000",
  headers: {
    "Content-Type": "application/json",
  },
});

generateBridgeRequestInterceptors(HTTPVimService);
generateBridgeRequestInterceptors(HTTPVimLocalService);

const FHIRSearch = (module, params) => {
  return HTTPRequest.get(
    `fhir/tenants/${getTenantFromToken()}/search/${module}${
      params ? "?" + params : ""
    }`
  );
};

const FHIRSave = (action, data) => {
  if (action === "executeBundle") {
    return HTTPRequest?.post(
      `fhir/tenants/${getTenantFromToken()}/` + "executeBundle",
      data
    );
  }
  let methodIdx = FHIR_METHODS.findIndex((f) => f.TYPE === action);
  if (methodIdx < 0) {
    console.error(
      `API Call Failed --> Please check if ${action} is a valid action.`
    );
    return false;
  }
  return HTTPRequest?.[_.lowerCase(action)](
    `fhir/tenants/${getTenantFromToken()}/` + FHIR_METHODS[methodIdx].RESOURCE,
    data
  );
};

const FHIRDelete = (resourceType, resourceId) => {
  return HTTPRequest?.delete(
    `fhir/tenants/${getTenantFromToken()}/deleteResource?resourceType=${resourceType}&resourceId=${resourceId}`
  );
};

const NokiService = (action, url = "", data) => {
  return HTTPAidaBaseService?.[_.lowerCase(action)](url, data);
};

const HTTPMetadata = (action, module, params = ``, payload = {}) => {
  const token = localStorage.getItem(`jwtToken`);
  let jwtPayload = {};
  if (token) jwtPayload = jwtDecode(token);
  let MetaHTTPRequest = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
  });
  generateMetaInterceptors(MetaHTTPRequest);
  return MetaHTTPRequest?.[_.lowerCase(action)](
    `metadata/v1/${jwtPayload?.firebase?.tenant}/${module}${
      params ? "?" + params : ""
    }`,
    payload
  );
};

export default HTTPRequest;
export {
  FHIRSearch,
  FHIRSave,
  FHIRDelete,
  NokiService,
  HTTPMetadata,
  HTTPRequestUnauthenticated,
};